import { useEffect, useState } from 'react';
import { PaymentMethodServerProps } from '../../lib/typegen/payment-method-server-props';
import style from './style.module.scss';
import { ListOptionItem } from '../list-options';
import { useStore } from '../../lib/hooks/useStore';
import { devices } from '../../lib/devices/devices';
import { DeviceStatusFlags } from '../../lib/devices/MonitoredDevice';
import GenericButton from '../button-generic';
import MessageBox from '../message-box';
import logger from '../../lib/helpers/logger';
import { PaymentButton } from '../../lib/typegen/payment-button';
import { PaymentMethodCheckResult } from '../../lib/stores/kioskStore';
import { getPPBehavior } from '../../lib/integrations';

type PaymentMethodProps = PaymentMethodServerProps;

export default function PaymentMethods({ card, cash, check, note, underLimitNote, overLimitNote }: PaymentMethodProps) {
	const [items, setItems] = useState<ListOptionItem[]>([]);
	const {
		configStore: { getString, envDeviceOption, config, ppType },
		uiStore: { handleNavigation },
		kioskStore: { canAcceptCashPayments, canPayAmountWithPaymentMethod },
		accountStore: { cartTotalAmount, setHasPreparedTransaction },
	} = useStore();

	const checkResult = canPayAmountWithPaymentMethod(cartTotalAmount, check.paymentTypes);
	const cardResult = canPayAmountWithPaymentMethod(cartTotalAmount, card.paymentTypes);
	const cashResult = canPayAmountWithPaymentMethod(cartTotalAmount, cash.paymentTypes);
	const checkEnabled = check.isEnabled ?? true;
	const cardEnabled = card.isEnabled ?? true;
	const cashEnabled = cash.isEnabled ?? true;

	const results: PaymentMethodCheckResult[] = [checkResult, cardResult, cashResult];
	const hasOverLimit = results.some((r) => r.limitReason === 'over-limit');
	const hasUnderLimit = results.some((r) => r.limitReason === 'under-limit');

	useEffect(() => {
		setHasPreparedTransaction(false); // Reset transaction preparation flag when payment methods change
		let checkReaderOk =
			devices.checkReader.getStatus().statusBits === DeviceStatusFlags.Ok && (config.ignoreCheckSensor || !devices.checkReader.checkInserted());
		logger.logGeneral.log({}, 'Check reader status: ', checkReaderOk);
		if (envDeviceOption === 'with-fallback' || envDeviceOption === 'never') checkReaderOk = true;

		const ppBehavior = getPPBehavior(ppType);
		let cardDeviceOk = ppBehavior.isCardDeviceAvailable?.() !== false;
		logger.logGeneral.log({}, 'Card device available: ', cardDeviceOk);
		if (envDeviceOption === 'with-fallback' || envDeviceOption === 'never') cardDeviceOk = true;

		const paymentMethods: { paymentButton: PaymentButton<string>; isEnabled: boolean }[] = [];
		if (cardDeviceOk && card) paymentMethods.push({ paymentButton: card, isEnabled: cardEnabled && cardResult.canPay });
		if (cash && canAcceptCashPayments) paymentMethods.push({ paymentButton: cash, isEnabled: cashEnabled && cashResult.canPay });
		if (checkReaderOk && check) paymentMethods.push({ paymentButton: check, isEnabled: checkEnabled && checkResult.canPay });

		const options = paymentMethods.map(({ paymentButton: { icon, label, target: linkTarget }, isEnabled }) => {
			const obj = {
				id: label,
				icon,
				label,
				linkTarget,
				isEnabled,
			};

			return obj;
		});
		setItems(options);
	}, [
		canAcceptCashPayments,
		canPayAmountWithPaymentMethod,
		card,
		cardResult.canPay,
		cartTotalAmount,
		cash,
		cashEnabled,
		cashResult.canPay,
		check,
		checkEnabled,
		checkResult.canPay,
		envDeviceOption,
		config.ignoreCheckSensor,
		cardEnabled,
		setHasPreparedTransaction,
		ppType,
	]);

	return (
		<section className={style.section}>
			<div className={style.section__methods}>
				{items.map((item, index) => (
					<GenericButton
						key={index}
						innerText={getString(item.label)}
						icon={item.icon}
						design='flush-left'
						onClick={() => {
							handleNavigation(item.linkTarget!);
						}}
						disabled={!item.isEnabled}
					/>
				))}
			</div>
			{hasOverLimit ? (
				<MessageBox className={style.section__note} type={'error'}>
					{getString(overLimitNote ?? 'method-of-payment-over-max')}
				</MessageBox>
			) : hasUnderLimit ? (
				<MessageBox className={style.section__note} type={'error'}>
					{getString(underLimitNote ?? 'method-of-payment-under-min')}
				</MessageBox>
			) : note ? (
				<MessageBox className={style.section__note} type={'warning'}>
					{getString(note)}
				</MessageBox>
			) : null}
		</section>
	);
}
